// Slideshow section JS. Per-section instance keyed by data-section-id.
//
// Cohesion notes:
// - Sections are discovered via [data-slideshow-new] attribute (matches the
//   collection-tabs pattern). Multiple slideshows on a page each get their
//   own instance.
// - CTA hover (bg/text/border invert) is handled in CSS, not here — the
//   section's :hover rules swap the per-block --cta-bg / --cta-text custom
//   properties with !important, so the outline tracks the text color through
//   the invert. This JS intentionally does not touch CTA hover.
// - Progress bar implementation preserves the pause/resume semantics of
//   the original — pause on hover, resume from the elapsed offset, not
//   restart from zero.

(function () {
  'use strict';

  function initSection(sectionEl) {
    if (!sectionEl || sectionEl.dataset.slideshowInitialized === 'true') return;
    sectionEl.dataset.slideshowInitialized = 'true';

    const sectionId = sectionEl.dataset.sectionId;
    const slideDuration = parseInt(sectionEl.dataset.slideDuration, 10);
    const animationStyle = sectionEl.dataset.animationStyle;

    const slideshow = sectionEl.querySelector(`#slideshow-${sectionId}`);
    if (!slideshow) return;

    const slides = sectionEl.querySelectorAll('.slideshow__slide');
    const paginationDots = sectionEl.querySelectorAll('.slideshow__dot');
    const mobileButtons = sectionEl.querySelectorAll('.slideshow__cta-mobile');
    const prevArrow = sectionEl.querySelector('.swiper-btn-prev');
    const nextArrow = sectionEl.querySelector('.swiper-btn-next');

    let currentIndex = 0;
    let autoplayInterval = null;
    let autoplayTimeout = null;
    let progressInterval = null;
    let progressStartTime = null;
    let pausedElapsed = 0;

    function updateProgressBar() {
      if (!progressStartTime) return;
      const elapsed = Date.now() - progressStartTime;
      const percentage = Math.min((elapsed / slideDuration) * 100, 100);
      paginationDots.forEach((dot) => {
        const dotIndex = parseInt(dot.dataset.paginationIndex, 10);
        const progress = dot.querySelector('.slideshow__dot-progress');
        if (!progress) return;
        // Use setProperty with 'important' so an inline !important wins
        // against any external rule that might be overriding width on
        // elements with this positioning shape. Mirrors the !important
        // wall in the CSS for the same reason — see the comment there.
        const value = (dotIndex === currentIndex ? percentage : 0) + '%';
        progress.style.setProperty('width', value, 'important');
      });
    }

    function startProgressBar(resumeFromPause = false) {
      if (resumeFromPause && pausedElapsed > 0) {
        progressStartTime = Date.now() - pausedElapsed;
        pausedElapsed = 0;
      } else {
        progressStartTime = Date.now();
      }
      updateProgressBar();
      if (progressInterval) clearInterval(progressInterval);
      progressInterval = setInterval(updateProgressBar, 50);
    }

    function stopProgressBar(isPause = false) {
      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }
      if (isPause && progressStartTime) {
        pausedElapsed = Date.now() - progressStartTime;
      } else {
        progressStartTime = null;
        pausedElapsed = 0;
      }
    }

    function goToSlide(index) {
      if (index < 0 || index >= slides.length || index === currentIndex) return;
      const previousIndex = currentIndex;
      currentIndex = index;

      slides.forEach((slide, i) => {
        slide.classList.remove('is-active', 'is-previous');
        if (i === currentIndex) {
          slide.classList.add('is-active');
        } else if (i === previousIndex && animationStyle === 'slide') {
          slide.classList.add('is-previous');
        }
      });

      paginationDots.forEach((dot) => {
        const dotIndex = parseInt(dot.dataset.paginationIndex, 10);
        dot.classList.toggle('is-active', dotIndex === currentIndex);
      });

      mobileButtons.forEach((btn, i) => {
        btn.classList.toggle('is-active', i === currentIndex);
      });

      // Fresh restart for new slide.
      stopProgressBar(false);
      startProgressBar(false);
    }

    function nextSlide() {
      goToSlide((currentIndex + 1) % slides.length);
    }
    function prevSlide() {
      goToSlide((currentIndex - 1 + slides.length) % slides.length);
    }

    function startAutoplay(resumeWithRemainingTime = false) {
      if (slideDuration <= 0) return;
      if (autoplayInterval) {
        clearInterval(autoplayInterval);
        autoplayInterval = null;
      }
      if (autoplayTimeout) {
        clearTimeout(autoplayTimeout);
        autoplayTimeout = null;
      }

      if (resumeWithRemainingTime && pausedElapsed > 0) {
        const remainingTime = slideDuration - pausedElapsed;
        if (remainingTime > 0) {
          autoplayTimeout = setTimeout(() => {
            nextSlide();
            autoplayInterval = setInterval(nextSlide, slideDuration);
          }, remainingTime);
        } else {
          nextSlide();
          autoplayInterval = setInterval(nextSlide, slideDuration);
        }
      } else {
        autoplayInterval = setInterval(nextSlide, slideDuration);
      }
    }

    function stopAutoplay() {
      if (autoplayInterval) {
        clearInterval(autoplayInterval);
        autoplayInterval = null;
      }
      if (autoplayTimeout) {
        clearTimeout(autoplayTimeout);
        autoplayTimeout = null;
      }
    }

    function restartAutoplay() {
      stopAutoplay();
      stopProgressBar(false);
      startAutoplay(false);
      startProgressBar(false);
    }

    // Pagination dot click → goto slide
    paginationDots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = parseInt(dot.dataset.paginationIndex, 10);
        goToSlide(index);
        restartAutoplay();
      });
    });

    // Arrow click → prev/next slide
    if (prevArrow) {
      prevArrow.addEventListener('click', () => {
        prevSlide();
        restartAutoplay();
      });
    }
    if (nextArrow) {
      nextArrow.addEventListener('click', () => {
        nextSlide();
        restartAutoplay();
      });
    }

    // Slide background click → navigate to slide's link (if set).
    // Skip if the click was on the inner CTA button or an arrow.
    slides.forEach((slide) => {
      slide.addEventListener('click', (e) => {
        if (e.target.closest('.slideshow__cta-desktop')) return;
        if (e.target.closest('.slideshow__btn')) return;
        const link = slide.dataset.slideLink;
        if (link) {
          window.location.href = link;
        }
      });
    });

    // CTA hover (bg/text/border invert) is owned entirely by CSS now — the
    // section's :hover rules swap the per-block --cta-bg / --cta-text custom
    // properties with !important, so the outline tracks the text color
    // through the invert without any JS. The old setupCtaHover handler was
    // removed to avoid doing redundant work that the CSS already overrides.

    // Initial state
    if (slides[0]) slides[0].classList.add('is-active');
    paginationDots.forEach((dot) => {
      const dotIndex = parseInt(dot.dataset.paginationIndex, 10);
      dot.classList.toggle('is-active', dotIndex === 0);
    });
    if (mobileButtons[0]) mobileButtons[0].classList.add('is-active');

    startProgressBar(false);
    startAutoplay(false);

    // Touch swipe support — primary nav method on mobile (where the tall-
    // rect arrow buttons hide). 50px minimum distance prevents accidental
    // navigation from minor finger drift. Listeners are passive: true
    // because we don't preventDefault, so the browser can still scroll
    // vertically through the section if the user happens to swipe at a
    // slight diagonal.
    function setupTouchSwipe() {
      let startX = 0;
      const minDistance = 50;
      slideshow.addEventListener(
        'touchstart',
        (e) => {
          startX = e.changedTouches[0].screenX;
        },
        { passive: true }
      );
      slideshow.addEventListener(
        'touchend',
        (e) => {
          const endX = e.changedTouches[0].screenX;
          const diff = startX - endX;
          if (Math.abs(diff) <= minDistance) return;
          if (diff > 0) {
            nextSlide();
          } else {
            prevSlide();
          }
          restartAutoplay();
        },
        { passive: true }
      );
    }
    setupTouchSwipe();

    // Pause on hover anywhere interactive
    function addPauseHandlers(element) {
      if (!element) return;
      element.addEventListener('mouseenter', () => {
        stopAutoplay();
        stopProgressBar(true);
      });
      element.addEventListener('mouseleave', () => {
        startAutoplay(true);
        startProgressBar(true);
      });
    }
    addPauseHandlers(slideshow);
    addPauseHandlers(sectionEl.querySelector('.slideshow__mobile-cta-container'));
    addPauseHandlers(sectionEl.querySelector('.slideshow__pagination--mobile'));
    addPauseHandlers(prevArrow);
    addPauseHandlers(nextArrow);
  }

  function initAll(root = document) {
    root.querySelectorAll('[data-slideshow-new]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  // Theme editor: pick up dynamically loaded section instances.
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      initAll(event.target);
    });
  }
})();
